import type { UIMessage } from "ai";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Volume2, Check, SmilePlus, Star, Copy } from "lucide-react";
import { memo, useState, useRef, useCallback } from "react";
import { Waveform } from "./Waveform";
import { SpeedPill } from "./SpeedPill";
import { Avatar } from "./Avatar";
import { ReactionPicker } from "./ReactionPicker";
import { ReactionDisplay } from "./ReactionDisplay";
import { InlineToolPanel } from "./InlineToolPanel";
import { ToolCardDispatch } from "./cards/ToolCardDispatch";
import { InlineThinking } from "./InlineThinking";
import { isToolPart } from "../shared/types";
import type { ToolUIPart } from "../shared/types";
import { AudioCache } from "../voice/audio-cache";
import type { Reaction } from "../hooks/useReactions";
import type { MessageMetrics, QueuedInfo } from "../hooks/useChat";
import type { ModelEntry } from "../shared/chat-config";
import { formatMessageStamp } from "../shared/model-facts";
import {
  formatTokenCount,
  formatDuration,
  formatTimestamp,
} from "../utils/format";
import { stripMarkdown } from "../../shared/strip-markdown.js";

// ─── Metrics chip ─────────────────────────────────────────────────────────────

interface MetricsChipProps {
  metrics: MessageMetrics;
}

function MetricsChip({ metrics }: MetricsChipProps) {
  const [expanded, setExpanded] = useState(false);

  const inTok = formatTokenCount(metrics.input_tokens);
  const outTok = formatTokenCount(metrics.output_tokens);
  const dur = formatDuration(metrics.duration_ms);

  const hasCacheRead = (metrics.cache_read_input_tokens ?? 0) > 0;
  const hasCacheCreate = (metrics.cache_creation_input_tokens ?? 0) > 0;

  return (
    <div className="msg-metrics-chip-wrapper">
      <button
        className="msg-metrics-chip"
        onClick={() => setExpanded((v) => !v)}
        title="Token usage and timing. Tap to expand."
        aria-label="Message metrics"
        aria-expanded={expanded}
      >
        <span aria-hidden="true">&#x2193;</span>{inTok}{" "}
        <span aria-hidden="true">&#x2191;</span>{outTok}
        {" · "}{dur}
      </button>
      {expanded && (
        <div className="msg-metrics-popover" role="tooltip">
          <div className="msg-metrics-row">
            <span>Input</span><span>{metrics.input_tokens.toLocaleString()}</span>
          </div>
          <div className="msg-metrics-row">
            <span>Output</span><span>{metrics.output_tokens.toLocaleString()}</span>
          </div>
          {hasCacheRead && (
            <div className="msg-metrics-row">
              <span>Cache read</span><span>{metrics.cache_read_input_tokens!.toLocaleString()}</span>
            </div>
          )}
          {hasCacheCreate && (
            <div className="msg-metrics-row">
              <span>Cache write</span><span>{metrics.cache_creation_input_tokens!.toLocaleString()}</span>
            </div>
          )}
          <div className="msg-metrics-row">
            <span>Duration</span><span>{formatDuration(metrics.duration_ms)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Copy button ──────────────────────────────────────────────────────────────

interface CopyButtonProps {
  /** Raw markdown content to copy (alt-click / long-press). */
  rawContent: string;
  /** Plain-text content to copy (default tap/click). */
  plainContent: string;
}

function CopyButton({ rawContent, plainContent }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const longPressDidFire = useRef(false);

  const doCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => {
      // Clipboard API unavailable (e.g. old iOS Safari) — degrade silently
    });
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Long-press already handled; suppress the click event that follows
    if (longPressDidFire.current) {
      longPressDidFire.current = false;
      return;
    }
    doCopy(e.altKey ? rawContent : plainContent);
  }, [doCopy, rawContent, plainContent]);

  const handlePointerDown = useCallback(() => {
    longPressDidFire.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressDidFire.current = true;
      doCopy(rawContent);
    }, 500);
  }, [doCopy, rawContent]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  return (
    <>
      <button
        className={`msg-copy-btn${copied ? " msg-copy-btn--copied" : ""}`}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        aria-label={copied ? "Copied" : "Copy message"}
      >
        {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
      </button>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </>
  );
}

interface ChatMessageProps {
  message: UIMessage;
  companionName?: string;
  userName?: string;
  onSpeak?: (message: UIMessage) => void;
  speakingMessageId?: string | null;
  audioCache?: AudioCache;
  voiceId?: string;
  onSeek?: (messageId: string, fraction: number) => void;
  getPlaybackProgress?: () => number;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
  /** True when Rook has replied after this user message (shows sage double-check). */
  isRead?: boolean;
  onSendMessage?: (text: string) => void;
  reactions?: Reaction[];
  onReact?: (messageId: string, emoji: string) => void;
  isKeepsaked?: boolean;
  onKeepsake?: (messageId: string) => void;
  metrics?: MessageMetrics;
  /** Common Room provenance (slice 1): 'hermes_wake' | 'reminder'. Renders a
   *  quiet footer chip from THIS prop only — never inferred from the message
   *  text itself (a wake whose content happens to contain the chip's own
   *  words must not forge the chip). Unrecognized values render nothing. */
  origin?: string;
  /** Session-bound wakes (slice 1): the resolved label of the chat this wake
   *  was scheduled from. Renders as a second span appended to the provenance
   *  chip — `· from "<label>"`, or `· from an untitled chat` when null.
   *  undefined (prop absent) renders nothing. Only ever shown alongside a
   *  recognized `origin` — an unrecognized origin renders neither. Plain
   *  text, never matched against PROVENANCE_LABELS — a label whose text
   *  happens to collide with a provenance string must not forge a second
   *  chip. */
  originSessionLabel?: string | null;
  /** C4 — the live registry, used to resolve metrics.model (the MUSE
   *  selection id the turn ran on) into a label + substrate for the
   *  per-message footer stamp. Same array the picker renders from. */
  availableModels?: ModelEntry[];
  /** When false, all reasoning/thinking blocks are hidden (non-destructive: parts
   *  stay in message state, toggling back on re-reveals them). Default: true. */
  showReasoning?: boolean;
  /** Email Composer v2 — see MessageListProps.sessionId. */
  sessionId?: string | null;
  /** ADR-023: set while this user message waits to be read. Dimmed, labelled, and
   *  takeable-back only when the server still holds it. */
  queued?: QueuedInfo;
  onTakeBack?: (messageId: string) => void;
}

// Extract the companion ID for avatar lookup (rook/rainer) from the display name.
// Known limitation: this is a bare .toLowerCase() — a display name with spaces
// or diacritics (a future multi-word companion name) won't match an Avatar.tsx
// PORTRAITS key and will silently fall through to the initial-letter avatar.
// Fine for today's single-word names; revisit if that ships.
function companionIdFor(name: string): string {
  return name.toLowerCase();
}

// Common Room provenance (slice 1). Maps the messages.origin column to its
// display label — deliberately a closed map, not a passthrough of the raw
// value: an unrecognized/future origin renders nothing rather than leaking
// an internal tag into the UI.
const PROVENANCE_LABELS: Record<string, string> = {
  hermes_wake: "Scheduled wake · via Hermes",
  // ADR-024: the result card of a wake that ran as the real companion, and the
  // server-built first turn of the wake chat itself.
  door_wake: "Scheduled wake · ran as Claude",
  door_wake_prompt: "Wake note · written by the companion",
  reminder: "Reminder",
};

function provenanceLabel(origin: string | undefined): string | undefined {
  return origin ? PROVENANCE_LABELS[origin] : undefined;
}

/**
 * Shared, safe chat-markdown renderer for both sides of the conversation.
 *
 * ReactMarkdown does not enable raw HTML unless rehype-raw is installed (it is
 * deliberately absent here), so user-authored text cannot become live DOM.
 * Remark also keeps emphasis inside inline/fenced code as code, which means
 * `*args` never receives the embodied-action treatment.
 */
function MessageMarkdown({ text }: { text: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children, ...props }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          >
            {children}
          </a>
        ),
        // `*tilts head*` / `*leans forward*` — embodied action language.
        // This renderer is shared by user and companion messages on purpose:
        // both people in the conversation get the same expressive vocabulary.
        em: ({ children, ...props }) => (
          <em className="msg-action-line" {...props}>
            {children}
          </em>
        ),
      }}
    >
      {text}
    </Markdown>
  );
}

/**
 * Filter reasoning parts based on the toggle state and the double-render guard.
 *
 * When showReasoning is false, all reasoning parts are dropped (the parts remain
 * in message state — toggling back ON re-reveals them without any data loss).
 *
 * When showReasoning is true (or undefined/default), the existing double-render
 * guard applies: if any reasoning part has real text (non-empty, non-whitespace,
 * not "[REDACTED]"), filter out reasoning parts whose text is empty,
 * whitespace-only, or "[REDACTED]". Otherwise return parts unchanged.
 */
function getVisibleParts(parts: UIMessage['parts'], showReasoning: boolean): UIMessage['parts'] {
  if (!showReasoning) {
    return parts.filter((p) => p.type !== 'reasoning');
  }
  const hasRealReasoning = parts.some(
    (p) =>
      p.type === 'reasoning' &&
      (p as { text?: string }).text?.trim() &&
      (p as { text?: string }).text !== '[REDACTED]',
  );
  if (!hasRealReasoning) return parts;
  return parts.filter(
    (p) =>
      p.type !== 'reasoning' ||
      ((p as { text?: string }).text?.trim() && (p as { text?: string }).text !== '[REDACTED]'),
  );
}

export const ChatMessage = memo(function ChatMessage({
  message,
  companionName = "Rook",
  userName = "Falco",
  onSpeak,
  speakingMessageId,
  audioCache,
  voiceId,
  onSeek,
  getPlaybackProgress,
  playbackSpeed = 1.0,
  onSpeedChange,
  isRead = false,
  reactions = [],
  onReact,
  onSendMessage,
  isKeepsaked = false,
  onKeepsake,
  metrics,
  origin,
  originSessionLabel,
  availableModels,
  showReasoning,
  sessionId,
  queued,
  onTakeBack,
}: ChatMessageProps) {
  const isUser = message.role === "user";
  const hasVoice = !!(audioCache && voiceId && onSeek && getPlaybackProgress);
  const ts = formatTimestamp(message.createdAt);
  const [pickerOpen, setPickerOpen] = useState(false);

  function handleReaction(emoji: string) {
    onReact?.(message.id, emoji);
    setPickerOpen(false);
  }

  const displayName = isUser ? userName : companionName;
  const avatarId = isUser ? "falco" : companionIdFor(companionName);

  const className = `msg-row ${isUser ? "msg-row-user" : "msg-row-assistant"}${queued ? " msg-row-queued" : ""}`;

  // ─── USER ─────────────────────────────────────────────────────────────
  if (isUser) {
    const textParts = message.parts
      .filter((p): p is Extract<typeof p, { type: "text" }> => p.type === "text");
    const text = textParts.map((p) => p.text).join("");

    const fileParts = message.parts
      .filter((p) => p.type === "file") as Array<{
        type: "file";
        mediaType: string;
        filename?: string;
        url?: string;
      }>;

    if (!text && fileParts.length === 0) return null;

    return (
      <div className={className} data-message-id={message.id}>
        <div className="msg-header">
          <Avatar identity={avatarId} size={22} />
          <span className="msg-name">{displayName}</span>
        </div>
        <div className="msg-bubble msg-bubble-user">
          {text && (
            <div className="msg-text message-content">
              <MessageMarkdown text={text} />
            </div>
          )}
          {fileParts.length > 0 && (
            <div className="message-attachments">
              {fileParts.map((fp, i) =>
                fp.mediaType?.startsWith("image/") && fp.url ? (
                  <img
                    key={i}
                    src={fp.url}
                    alt={fp.filename ?? "image"}
                    className="message-attachment-image"
                  />
                ) : (
                  <span key={i} className="message-attachment-badge">
                    {fp.filename ?? "file"}
                  </span>
                ),
              )}
            </div>
          )}
          <ReactionDisplay reactions={reactions} onToggle={handleReaction} />
          <div className="msg-footer-controls">
            {onReact && (
              <div className="msg-reaction-trigger">
                <button
                  className="msg-reaction-btn"
                  onClick={() => setPickerOpen(o => !o)}
                  aria-label="Add reaction"
                >
                  <SmilePlus size={14} />
                </button>
                {pickerOpen && (
                  <ReactionPicker onPick={handleReaction} onClose={() => setPickerOpen(false)} />
                )}
              </div>
            )}
            {onKeepsake && (
              <button
                className={`keepsake-btn${isKeepsaked ? " active" : ""}`}
                onClick={() => onKeepsake(message.id)}
                aria-label={isKeepsaked ? "Remove keepsake" : "Save as keepsake"}
                aria-pressed={!!isKeepsaked}
              >
                <Star size={16} fill={isKeepsaked ? "currentColor" : "none"} />
              </button>
            )}
          </div>
          {queued && (
            <div className="msg-queued-note" role="status">
              <span>{"He'll read it at his next pause"}</span>
              {queued.takeBack && onTakeBack && (
                <button
                  type="button"
                  className="msg-queued-takeback"
                  onClick={() => onTakeBack(message.id)}
                >
                  Take back
                </button>
              )}
            </div>
          )}
          {ts && (
            <span className={`msg-timestamp ${isRead ? "msg-timestamp--read" : ""}`}>
              {ts}
              <span className="msg-checks" aria-label={isRead ? `${companionName} replied` : "Sent"}>
                {isRead ? (
                  <span className="msg-check-double">
                    <Check size={10} strokeWidth={2.5} aria-hidden="true" />
                    <Check size={10} strokeWidth={2.5} aria-hidden="true" />
                  </span>
                ) : (
                  <Check size={10} strokeWidth={2.5} aria-hidden="true" />
                )}
              </span>
            </span>
          )}
        </div>
      </div>
    );
  }

  // ─── ASSISTANT ────────────────────────────────────────────────────────
  const hasContent = message.parts.some(
    (p) =>
      p.type === "text" ||
      p.type === "reasoning" ||
      p.type === "step-start" ||
      isToolPart(p as { type: string }),
  );
  if (!hasContent) return null;

  // Collect raw markdown from text parts for the copy button
  const rawMarkdown = message.parts
    .filter((p): p is Extract<typeof p, { type: "text" }> => p.type === "text")
    .map((p) => p.text)
    .join("\n\n");

  // C4 — per-message model stamp. metrics.model is only ever populated for
  // turns completed during THIS page load (server.ts stamps it on the live
  // response_metrics frame; a replayed/historical turn from before reload
  // never carries it) — the same reload-loses-it precedent MetricsChip's
  // token/cost figures above already accept for this codebase.
  const modelForMessage = metrics?.model
    ? availableModels?.find((m) => m.id === metrics.model)
    : undefined;
  const provenance = provenanceLabel(origin);

  return (
    <div className={className} data-message-id={message.id}>
      <div className="msg-header">
        <Avatar identity={avatarId} size={22} />
        <span className="msg-name">{displayName}</span>
      </div>
      <div className="msg-bubble msg-bubble-assistant">
        <div className="message-content">
          {getVisibleParts(message.parts, showReasoning ?? true).map((part, i) => {
            if (part.type === "reasoning") {
              const text = (part as { text: string }).text || "[REDACTED]";
              return <InlineThinking key={i} text={text} />;
            }
            if (part.type === "step-start") return null;
            if (isToolPart(part as { type: string })) {
              const toolPart = part as unknown as ToolUIPart;
              // Keyed on toolCallId, not the array index `i` — an index key lets
              // React reuse a card's local state (e.g. EmailDraftCard's edit/save
              // state) across an unrelated tool call when parts are inserted/
              // reordered ahead of it (the cross-contamination class Eli flagged).
              if (onSendMessage) {
                return (
                  <ToolCardDispatch
                    key={toolPart.toolCallId}
                    part={toolPart}
                    onSendMessage={onSendMessage}
                    sessionId={sessionId}
                  />
                );
              }
              return <InlineToolPanel key={toolPart.toolCallId} part={toolPart} />;
            }
            if (part.type === "text") {
              const text = (part as { text: string }).text;
              if (!text) return null;
              return <MessageMarkdown key={i} text={text} />;
            }
            return null;
          })}
        </div>
        <div className="msg-footer-controls">
          {onSpeak && (
            <button
              className={`message-speak-btn ${
                speakingMessageId === message.id ? "speaking" : ""
              }`}
              onClick={() => onSpeak(message)}
              aria-label={speakingMessageId === message.id ? "Stop speaking" : "Speak message"}
              aria-pressed={speakingMessageId === message.id}
              title={
                speakingMessageId === message.id
                  ? "Stop speaking"
                  : "Speak message"
              }
            >
              <Volume2 size={13} />
            </button>
          )}
          {onSpeak && onSpeedChange && (
            <SpeedPill speed={playbackSpeed} onSpeedChange={onSpeedChange} />
          )}
          {hasVoice &&
            audioCache!.hasPartial(AudioCache.makeKey(message.id, voiceId!)) && (
              <Waveform
                messageId={message.id}
                voiceId={voiceId}
                audioCache={audioCache}
                isPlaying={speakingMessageId === message.id}
                onSeek={onSeek}
                getPlaybackProgress={getPlaybackProgress}
              />
            )}
          {onKeepsake && (
            <button
              className={`keepsake-btn${isKeepsaked ? " active" : ""}`}
              onClick={() => onKeepsake(message.id)}
              aria-label={isKeepsaked ? "Remove keepsake" : "Save as keepsake"}
              aria-pressed={!!isKeepsaked}
            >
              <Star size={16} fill={isKeepsaked ? "currentColor" : "none"} />
            </button>
          )}
        </div>
        {(metrics || modelForMessage || provenance) && (
          <div className="msg-footer-row">
            {provenance && (
              <span className="msg-model-stamp" data-testid="provenance-chip">
                {provenance}
                {originSessionLabel !== undefined && (
                  <span>
                    {originSessionLabel === null
                      ? ' · from an untitled chat'
                      : ` · from "${originSessionLabel}"`}
                  </span>
                )}
              </span>
            )}
            {modelForMessage && (
              <span className="msg-model-stamp">{formatMessageStamp(modelForMessage)}</span>
            )}
            {metrics && <MetricsChip metrics={metrics} />}
          </div>
        )}
        <ReactionDisplay reactions={reactions} onToggle={handleReaction} />
        <div className="msg-action-row">
          {onReact && (
            <div className="msg-reaction-trigger">
              <button
                className="msg-reaction-btn"
                onClick={() => setPickerOpen(o => !o)}
                aria-label="Add reaction"
              >
                <SmilePlus size={14} />
              </button>
              {pickerOpen && (
                <ReactionPicker onPick={handleReaction} onClose={() => setPickerOpen(false)} />
              )}
            </div>
          )}
          {rawMarkdown && (
            <CopyButton
              rawContent={rawMarkdown}
              plainContent={stripMarkdown(rawMarkdown)}
            />
          )}
        </div>
        {ts && <span className="msg-timestamp">{ts}</span>}
      </div>
    </div>
  );
});
